import { config } from '../config';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const RECAPTCHA_SCRIPT_ID = 'klaris-recaptcha-v3';
const RECAPTCHA_BASE_URL = 'https://www.google.com/recaptcha/api.js';

let recaptchaLoadPromise: Promise<void> | null = null;

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (window.grecaptcha) return Promise.resolve();
  if (recaptchaLoadPromise) return recaptchaLoadPromise;

  recaptchaLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.grecaptcha) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    script.src = `${RECAPTCHA_BASE_URL}?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(script);
  });

  return recaptchaLoadPromise;
}

export async function executeRecaptcha(action: string): Promise<string | null> {
  const siteKey = config.recaptchaSiteKey;
  if (!siteKey) {
    return null;
  }

  await loadRecaptchaScript(siteKey);

  await new Promise<void>((resolve, reject) => {
    if (!window.grecaptcha) {
      reject(new Error('reCAPTCHA did not initialize. Please disable ad blockers and retry.'));
      return;
    }
    window.grecaptcha.ready(() => resolve());
  });

  const token = await window.grecaptcha?.execute(siteKey, { action });
  if (!token) {
    throw new Error('Captcha check failed. Please try again.');
  }
  return token;
}
